/**
 * Derivations for the Connections "Headcount / Attrition" Insights views
 * (replicating Saurabh's Canary connection-vs-attrition narrative on the live
 * TM1 -> RDS -> getConnectionsHeadcount pipeline).
 *
 * getConnectionsHeadcount returns long/tidy rows keyed on
 * (leaderLogin, metricName, metricMonth). metricName is the READABLE description
 * from dim_connections_headcount_metrics. This module pivots those rows into a
 * per-leader people-flow and derives the figures the charts need.
 *
 * Data facts (verified against Alpha fact_connections_headcount, 2026-07-14):
 *  - Outflows are stored NEGATIVE: `External Terminations` / `*Out` are < 0.
 *    Attrition therefore uses abs(terminations).
 *  - The stored `Attrition Pct` / `Attrition Pct - Annualized` metrics have
 *    ambiguous units across the org-size mix, so attrition is DERIVED from clean
 *    counts: abs(terminations) / beginning-active * 100 (monthly %).
 *  - Headcount trajectory anchor is `Active Workforce (Ending - Brady)`.
 *  - FY = calendar year (FY25 = 2025 months, FY26 = 2026 months). FY attrition is
 *    the mean of monthly attrition % within that year (Saurabh's "avg monthly
 *    attrition per leader").
 *  - The fact table may also contain unrelated app-usage / trend / tenure rows;
 *    we key strictly on the metricName constants below, so those are ignored.
 */
import { ConnectionsHeadcountPoint, ConnectionsScorecard } from '../types';

/** Readable metricName values (as returned by getConnectionsHeadcount). */
export const HC_METRIC = {
  begActive: 'Active Workforce (Beginning)',
  endActive: 'Active Workforce (Ending - Brady)',
  terms: 'External Terminations', // stored NEGATIVE
  termsTotal: 'Total External Terminations', // stored NEGATIVE
  hiresTotal: 'Total External Hires',
  netInternal: 'Total Internal Movements'
} as const;

export interface MonthValue {
  month: string; // 'YYYY-MM-DD' (first of month)
  value: number;
}

export interface LeaderPeopleFlow {
  login: string;
  name: string;
  /** Ending active headcount by month, ascending. */
  headcountMonthly: MonthValue[];
  /** Derived monthly attrition % (abs(terms) / beginning-active * 100), ascending. */
  attritionMonthly: MonthValue[];
  /** Net internal movement (Total Internal Movements = in - out) by month, ascending. */
  netInternalMonthly: MonthValue[];
  /** Mean monthly attrition % over the previous calendar year (null if none). */
  prevFyAttrition: number | null;
  /** Mean monthly attrition % over the current (latest) calendar year (null if none). */
  currentFyAttrition: number | null;
  /** current - previous mean monthly attrition % (acceleration; null if either missing). */
  attritionAcceleration: number | null;
  latestHeadcount: number | null;
  latestAttrition: number | null;
  /** Ending active[latest] - ending active[~6 months earlier] (null if <2 points). */
  headcountChange6mo: number | null;
  /** Percentage headcount change over the same window (null if no baseline). */
  headcountChangePct: number | null;
}

/** Normalize a metricMonth to a 'YYYY-MM-DD' key. */
function monthKey(m: string | null | undefined): string | null {
  if (!m) return null;
  return m.slice(0, 10);
}

/** Index one leader's points into metricName -> (month -> value). */
function indexByMetric(points: ConnectionsHeadcountPoint[]): Map<string, Map<string, number>> {
  const idx = new Map<string, Map<string, number>>();
  for (const p of points) {
    if (p.metricValue == null) continue;
    const mm = monthKey(p.metricMonth);
    if (!mm) continue;
    if (!idx.has(p.metricName)) idx.set(p.metricName, new Map());
    idx.get(p.metricName)!.set(mm, p.metricValue);
  }
  return idx;
}

function seriesFor(idx: Map<string, Map<string, number>>, metricName: string): MonthValue[] {
  const mm = idx.get(metricName);
  if (!mm) return [];
  return [...mm.entries()].map(([month, value]) => ({ month, value })).sort((a, b) => a.month.localeCompare(b.month));
}

/** Derived monthly attrition %: abs(terminations) / beginning-active * 100. */
function deriveAttrition(idx: Map<string, Map<string, number>>): MonthValue[] {
  const terms = idx.get(HC_METRIC.terms) || idx.get(HC_METRIC.termsTotal);
  const beg = idx.get(HC_METRIC.begActive);
  if (!terms || !beg) return [];
  const out: MonthValue[] = [];
  for (const [month, t] of terms.entries()) {
    const b = beg.get(month);
    if (b && b > 0) out.push({ month, value: (Math.abs(t) / b) * 100 });
  }
  return out.sort((a, b) => a.month.localeCompare(b.month));
}

function meanForYear(series: MonthValue[], year: string): number | null {
  const ys = series.filter((s) => s.month.startsWith(year));
  if (!ys.length) return null;
  return ys.reduce((sum, x) => sum + x.value, 0) / ys.length;
}

/** Whole-month distance between two 'YYYY-MM-DD' keys (b - a). */
function monthsBetween(a: string, b: string): number {
  const [ay, am] = a.split('-').map(Number);
  const [by, bm] = b.split('-').map(Number);
  return (by - ay) * 12 + (bm - am);
}

/** Latest calendar year present in the data (falls back to the current year if empty). */
export function latestYear(points: ConnectionsHeadcountPoint[]): number {
  let max = 0;
  for (const p of points) {
    const mm = monthKey(p.metricMonth);
    if (!mm) continue;
    const y = Number(mm.slice(0, 4));
    if (y > max) max = y;
  }
  return max || new Date().getFullYear();
}

/** Build a per-leader people-flow from that leader's headcount points. */
export function buildPeopleFlow(login: string, name: string, points: ConnectionsHeadcountPoint[], currentYear: number): LeaderPeopleFlow {
  const idx = indexByMetric(points);
  const headcountMonthly = seriesFor(idx, HC_METRIC.endActive);
  const attritionMonthly = deriveAttrition(idx);
  const netInternalMonthly = seriesFor(idx, HC_METRIC.netInternal);

  const latestHeadcount = headcountMonthly.length ? headcountMonthly[headcountMonthly.length - 1].value : null;
  const latestAttrition = attritionMonthly.length ? attritionMonthly[attritionMonthly.length - 1].value : null;

  let headcountChange6mo: number | null = null;
  let headcountChangePct: number | null = null;
  if (headcountMonthly.length >= 2) {
    const last = headcountMonthly[headcountMonthly.length - 1];
    // Prefer the point exactly 6 months before the latest; else the earliest available.
    const sixPrior = headcountMonthly.find((p) => monthsBetween(p.month, last.month) === 6);
    const baseline = sixPrior || headcountMonthly[0];
    headcountChange6mo = last.value - baseline.value;
    headcountChangePct = baseline.value ? ((last.value - baseline.value) / baseline.value) * 100 : null;
  }

  const currentFyAttrition = meanForYear(attritionMonthly, String(currentYear));
  const prevFyAttrition = meanForYear(attritionMonthly, String(currentYear - 1));
  const attritionAcceleration = prevFyAttrition != null && currentFyAttrition != null ? currentFyAttrition - prevFyAttrition : null;

  return {
    login,
    name,
    headcountMonthly,
    attritionMonthly,
    netInternalMonthly,
    prevFyAttrition,
    currentFyAttrition,
    attritionAcceleration,
    latestHeadcount,
    latestAttrition,
    headcountChange6mo,
    headcountChangePct
  };
}

/**
 * Group flattened multi-leader points by login and build a people-flow per leader.
 * `names` maps login -> display name (falls back to the point's displayName/login).
 */
export function buildPeopleFlows(points: ConnectionsHeadcountPoint[], names?: Record<string, string>): LeaderPeopleFlow[] {
  const year = latestYear(points);
  const byLogin = new Map<string, ConnectionsHeadcountPoint[]>();
  for (const p of points) {
    if (!p.leaderLogin) continue;
    if (!byLogin.has(p.leaderLogin)) byLogin.set(p.leaderLogin, []);
    byLogin.get(p.leaderLogin)!.push(p);
  }
  const flows: LeaderPeopleFlow[] = [];
  for (const [login, pts] of byLogin.entries()) {
    const name = names?.[login] || pts.find((p) => p.displayName)?.displayName || login;
    flows.push(buildPeopleFlow(login, name, pts, year));
  }
  return flows;
}

/**
 * Connection score for a leader = mean of the (non-null) theme metric values,
 * expressed as a percentage. Theme metricValues are 0-1 fractions upstream, so
 * this returns a 0-100 value. Returns null if the leader has no scored themes.
 */
export function connectionScore(sc: ConnectionsScorecard | null | undefined): number | null {
  if (!sc || !sc.themes?.length) return null;
  const vals = sc.themes.map((t) => t.metricValue).filter((v): v is number => v != null);
  if (!vals.length) return null;
  return (vals.reduce((s, v) => s + v, 0) / vals.length) * 100;
}

/** Median of a numeric array (used for quadrant crosshairs). Null if empty. */
export function median(values: number[]): number | null {
  const a = values.filter((v) => v != null && !Number.isNaN(v)).sort((x, y) => x - y);
  if (!a.length) return null;
  const mid = Math.floor(a.length / 2);
  return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
}
